import type { CareerPlanCompetencyScore } from "./careerPlanHistory";
import { normalizeExperienceCompetencyName } from "./experienceIndexCategories";
import { getHubTopStrengths } from "./experienceIndexResults";

export type ExperienceArchetype = {
  description: string;
  growthEdgeName: string;
  id: string;
  strengthNames: string[];
  title: string;
};

// Keep ids, titles, named strengths, and growth edges in sync with membership-mobile-app
// `skillsIndexArchetypes.ts`. Descriptions are consultant third-person dialect.
export const EXPERIENCE_ARCHETYPES: ExperienceArchetype[] = [
  {
    id: "rising-coach",
    title: "The Rising Coach",
    strengthNames: ["Teaching and Coaching", "Technology"],
    growthEdgeName: "Network",
    description:
      "This person's answers point to a coach building real teaching reps and using technology to make feedback clearer. Growing a broader PGA network could help them turn that technical foundation into more opportunities to learn, collaborate, and lead."
  },
  {
    id: "tactical-craftsman",
    title: "The Tactical Craftsman",
    strengthNames: ["Teaching and Coaching", "Admin", "Technology"],
    growthEdgeName: "Public Speaking",
    description:
      "This person brings a structured, detail-minded approach to instruction and the systems around it. The next stretch is explaining that expertise with the same confidence in larger rooms and more formal settings, so others can understand and use what they know."
  },
  {
    id: "mentoring-pro",
    title: "The Mentoring Pro",
    strengthNames: ["Teaching and Coaching", "Network", "Personnel"],
    growthEdgeName: "Budget",
    description:
      "This person's experience centers on helping players and people improve, with strong relationship and team instincts behind it. Building more ownership of budgets would add the business range needed for their next leadership step and give that people-first work a stronger operating base."
  },
  {
    id: "relationship-builder",
    title: "The Relationship Builder",
    strengthNames: ["Network", "Teaching and Coaching", "Public Speaking"],
    growthEdgeName: "Admin",
    description:
      "This person creates momentum through trust, communication, and time spent helping others grow. Stronger administrative systems would help that people-first impact hold up as their responsibilities expand and free them to spend more time coaching and connecting."
  },
  {
    id: "program-architect",
    title: "The Program Architect",
    strengthNames: ["Golf Academy", "Personnel", "Admin"],
    growthEdgeName: "Financial Acumen",
    description:
      "This person shows the building blocks of someone who can turn individual work into repeatable programs and guide the people delivering them. Deepening the financial side would help them connect a strong program vision to durable business results."
  },
  {
    id: "academy-builder",
    title: "The Academy Builder",
    strengthNames: ["Golf Academy", "Teaching and Coaching", "Budget"],
    growthEdgeName: "Social Media",
    description:
      "This person's answers suggest they can pair player development with the practical work of running a program. A stronger digital presence could help more members discover that work, keep their academy pipeline active, and support steadier participation."
  },
  {
    id: "digital-coach",
    title: "The Digital Coach",
    strengthNames: ["Technology", "Social Media", "Teaching and Coaching"],
    growthEdgeName: "Golf Academy",
    description:
      "This person blends coaching with modern tools and knows how to make their work visible beyond the lesson tee. The next opportunity is turning that reach into a repeatable academy or multi-week player-development program that can grow beyond one-off moments."
  },
  {
    id: "operations-anchor",
    title: "The Operations Anchor",
    strengthNames: ["Operator", "Admin", "Tournament Operations"],
    growthEdgeName: "Personnel",
    description:
      "This person appears comfortable keeping complex operations organized and moving when the calendar gets busy. Building more experience developing staff would make those systems less dependent on them doing the heavy lifting and create room for higher-level decisions."
  },
  {
    id: "revenue-navigator",
    title: "The Revenue Navigator",
    strengthNames: ["Budget", "Financial Acumen", "Merchandise"],
    growthEdgeName: "Public Speaking",
    description:
      "This person connects day-to-day decisions to revenue, cost, and the health of the business. Sharpening how they present those insights could help their recommendations carry more weight and turn sound analysis into decisions others understand and support."
  },
  {
    id: "tournament-conductor",
    title: "The Tournament Conductor",
    strengthNames: ["Tournament Operations", "Admin", "Public Speaking"],
    growthEdgeName: "Financial Acumen",
    description:
      "This person's answers show someone who can organize event details, communicate clearly, and keep the day moving. Adding stronger financial analysis would help them evaluate whether an event ran well and how it performed for the facility."
  },
  {
    id: "member-experience-pro",
    title: "The Member Experience Pro",
    strengthNames: ["Food & Beverage", "Merchandise", "Network"],
    growthEdgeName: "Technology",
    description:
      "This person understands that a strong facility experience is built across service, retail, and relationships. More confidence with the systems behind those touchpoints could help them spot patterns, improve the experience at scale, and turn individual wins into consistent habits."
  },
  {
    id: "team-builder",
    title: "The Team Builder",
    strengthNames: ["Personnel", "Network", "Admin"],
    growthEdgeName: "Budget",
    description:
      "This person brings structure to people work and appears comfortable helping teams stay connected and accountable. More direct budget ownership would give them another tool for turning team priorities into operating decisions and making tradeoffs clearer when resources compete."
  },
  {
    id: "facility-generalist",
    title: "The Facility Generalist",
    strengthNames: ["Course Maintenance", "Food & Beverage", "Operator"],
    growthEdgeName: "Public Speaking",
    description:
      "This person's experience reaches across several parts of the facility, giving them a practical view of how the whole operation fits together. Developing a clearer executive voice could help them turn that broad perspective into influence."
  },
  {
    id: "strategic-storyteller",
    title: "The Strategic Storyteller",
    strengthNames: ["Public Speaking", "Social Media", "Network"],
    growthEdgeName: "Financial Acumen",
    description:
      "This person knows how to bring people into an idea, whether the room is live or digital. Pairing that communication strength with stronger financial evidence would make the story behind their recommendations even harder to ignore."
  },
  {
    id: "connected-operator",
    title: "The Connected Operator",
    strengthNames: ["Network", "Operator", "Technology"],
    growthEdgeName: "Admin",
    description:
      "This person combines industry relationships with a practical command of operating tools and systems. More disciplined documentation and deadline management would help that fast-moving style stay dependable as the work grows and make collaboration easier without slowing the pace."
  },
  {
    id: "retail-strategist",
    title: "The Retail Strategist",
    strengthNames: ["Merchandise", "Financial Acumen", "Social Media"],
    growthEdgeName: "Personnel",
    description:
      "This person connects product, numbers, and promotion in a way that can make the golf shop a stronger part of the business. The next stretch is building a team that can carry that strategy consistently without every decision routing through them."
  },
  {
    id: "course-steward",
    title: "The Course Steward",
    strengthNames: ["Course Maintenance", "Admin", "Budget"],
    growthEdgeName: "Network",
    description:
      "This person's answers suggest a grounded professional who respects both the condition of the facility and the planning required to support it. Expanding their peer network could bring in more benchmarks, ideas, and trusted help when larger decisions arrive."
  },
  {
    id: "hospitality-driver",
    title: "The Hospitality Driver",
    strengthNames: ["Food & Beverage", "Network", "Personnel"],
    growthEdgeName: "Technology",
    description:
      "This person creates value through service, relationships, and the people delivering the experience. Better use of facility data and systems could help them see what is working, make those service wins repeatable, and give their team evidence instead of guesses."
  },
  {
    id: "systems-builder",
    title: "The Systems Builder",
    strengthNames: ["Admin", "Technology", "Operator"],
    growthEdgeName: "Teaching and Coaching",
    description:
      "This person is strongest where processes, tools, and operating discipline come together. Building more coaching experience could help them transfer that knowledge so the system improves the people using it, too, and becomes easier for others to own."
  },
  {
    id: "growth-marketer",
    title: "The Growth Marketer",
    strengthNames: ["Social Media", "Golf Academy", "Public Speaking"],
    growthEdgeName: "Budget",
    description:
      "This person can make programs visible, explain their value, and create energy around participation. Stronger budget ownership would help them decide which growth ideas deserve investment, prove what they returned, and keep creative work tied to clear business choices."
  }
];

function completedCompetencyScores(scores: CareerPlanCompetencyScore[]) {
  return scores.filter((score) => score.isComplete && typeof score.score === "number");
}

function completedScoreByName(scores: CareerPlanCompetencyScore[]) {
  return new Map(
    completedCompetencyScores(scores).map((score) => [normalizeExperienceCompetencyName(score.competencyName), score])
  );
}

function topStrengthNames(scores: CareerPlanCompetencyScore[]) {
  return new Set(getHubTopStrengths(scores, 3).map((item) => normalizeExperienceCompetencyName(item.label)));
}

function overlapCount(archetype: ExperienceArchetype, topNames: Set<string>) {
  return archetype.strengthNames.filter((name) => topNames.has(normalizeExperienceCompetencyName(name))).length;
}

function growthEdgeInTop3(archetype: ExperienceArchetype, topNames: Set<string>) {
  return topNames.has(normalizeExperienceCompetencyName(archetype.growthEdgeName));
}

function meanNamedStrengthScore(archetype: ExperienceArchetype, scoresByName: Map<string, CareerPlanCompetencyScore>) {
  const namedScores = archetype.strengthNames.map(
    (name) => scoresByName.get(normalizeExperienceCompetencyName(name))?.score ?? 0
  );
  return namedScores.reduce((total, score) => total + score, 0) / namedScores.length;
}

function growthEdgeScore(archetype: ExperienceArchetype, scoresByName: Map<string, CareerPlanCompetencyScore>) {
  return scoresByName.get(normalizeExperienceCompetencyName(archetype.growthEdgeName))?.score ?? 0;
}

export function matchExperienceArchetype(scores: CareerPlanCompetencyScore[]): ExperienceArchetype | null {
  if (completedCompetencyScores(scores).length === 0) {
    return null;
  }

  const topNames = topStrengthNames(scores);
  const scoresByName = completedScoreByName(scores);

  return [...EXPERIENCE_ARCHETYPES].sort((left, right) => {
    const overlapDiff = overlapCount(right, topNames) - overlapCount(left, topNames);
    if (overlapDiff !== 0) {
      return overlapDiff;
    }

    const leftConflict = growthEdgeInTop3(left, topNames) ? 1 : 0;
    const rightConflict = growthEdgeInTop3(right, topNames) ? 1 : 0;
    if (leftConflict !== rightConflict) {
      return leftConflict - rightConflict;
    }

    const meanDiff = meanNamedStrengthScore(right, scoresByName) - meanNamedStrengthScore(left, scoresByName);
    if (meanDiff !== 0) {
      return meanDiff;
    }

    const growthDiff = growthEdgeScore(left, scoresByName) - growthEdgeScore(right, scoresByName);
    if (growthDiff !== 0) {
      return growthDiff;
    }

    return left.title.localeCompare(right.title);
  })[0];
}

export function getArchetypeStrengthScores(archetype: ExperienceArchetype, scores: CareerPlanCompetencyScore[]) {
  const scoresByName = completedScoreByName(scores);

  return archetype.strengthNames.flatMap((name) => {
    const score = scoresByName.get(normalizeExperienceCompetencyName(name));
    return score ? [score] : [];
  });
}
